import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, ActivityIndicator } from 'react-native';

import { GetDays, MONTH_NAME } from '@components/DatePicker/DatePicker';
import {
  ClearDateButton,
  HoverableDayCell,
} from '@components/DatePicker/HoverableDayCell';
import { Box, Text } from '@components/Restyle';
import Button from '@components/shared/Button/Button';
import { WebIcon } from '@components/shared/Icon/WebIcon';
import theme, { getCommonScaledDimensions } from '@themes/theme';
import { beginningOfToday } from '@utils/formatters/date';

import type { Dayjs } from 'dayjs';

type DateRangePickerMobileProps = {
  start?: Date;
  end?: Date;
  isSubmitting?: boolean;
  datePickerType?: 'start' | 'end' | 'range';
  setDatePickerType?: (x: 'start' | 'end' | 'range') => void;
  onSelectStartDate?: (start: Dayjs | undefined) => void;
  onSelectEndDate?: (end: Dayjs | undefined) => void;
  onSelect: (start: Date | undefined, end: Date | undefined) => void;
  /**
   * Drops the built-in Clear/Confirm row. The filter sheets (TA-10335 /
   * TA-10336) own a Reset/Apply footer of their own further down the sheet and
   * read the selection through `onSelectStartDate` / `onSelectEndDate`, so a
   * second pair of buttons inside the calendar would be duplicate CTAs.
   */
  hideFooter?: boolean;
  clearLabel?: string;
  confirmLabel?: string;
};
const CELL_SIZE = 32;
const CELL_RADIUS = CELL_SIZE / 2;
const ROW_PADDING_X = 16;

type DayCellProps = {
  displayNumber: number;
  isPrevMonth: boolean;
  isNextMonth: boolean;
  isSelectedCircle: boolean;
  isToday: boolean;
  showFullGrey: boolean;
  showHalfRightGrey: boolean;
  showHalfLeftGrey: boolean;
  onPress: () => void;
};

// TA-10950 #7/#26: grey01 hover on every selectable date.
const DayCell: React.FC<DayCellProps> = ({
  displayNumber,
  isPrevMonth,
  isNextMonth,
  isSelectedCircle,
  isToday,
  showFullGrey,
  showHalfRightGrey,
  showHalfLeftGrey,
  onPress,
}) => (
  <Box style={styles.dayColumn}>
    {(showFullGrey || showHalfRightGrey || showHalfLeftGrey) && (
      <Box
        style={[
          styles.rangeBackgroundBase,
          showFullGrey && styles.rangeBackgroundFull,
          showHalfRightGrey && styles.rangeBackgroundRightHalf,
          showHalfLeftGrey && styles.rangeBackgroundLeftHalf,
        ]}
      />
    )}

    <HoverableDayCell
      hoverDisabled={isSelectedCircle}
      onPress={onPress}
      cursor='pointer'
      accessibilityLabel={displayNumber > 0 ? `${displayNumber}` : undefined}
      radius={CELL_RADIUS}
      width={CELL_SIZE}
      height={CELL_SIZE}>
      <Box style={styles.dayCellWrapper}>
        {isSelectedCircle && <Box style={styles.selectedCircle} />}
        {!isSelectedCircle && isToday && <Box style={styles.todayOutline} />}

        <Text
          variant='webBodySecondary'
          style={[
            styles.dayLabel,
            (isPrevMonth || isNextMonth) && styles.dayLabelOutsideMonth,
            isSelectedCircle && styles.dayLabelSelected,
          ]}>
          {displayNumber > 0 ? displayNumber : ' '}
        </Text>
      </Box>
    </HoverableDayCell>
  </Box>
);

export const DateRangePickerMobile: React.FC<DateRangePickerMobileProps> = ({
  start,
  end,
  onSelect,
  datePickerType = 'range',
  setDatePickerType = () => {},
  isSubmitting = false,
  onSelectEndDate,
  onSelectStartDate,
  hideFooter = false,
  clearLabel,
  confirmLabel,
}) => {
  const date = beginningOfToday();
  const [year, setYear] = useState(date.getFullYear());
  const [month, setMonth] = useState(date.getMonth());

  const [currentMonthDay, setCurrentMonthDay] = useState<number[]>([]);
  const [daysInMonth, setDaysInMonth] = useState<number>(
    GetDays(date.getFullYear(), date.getMonth())
  );
  const { t } = useTranslation('format');
  const [startDate, setStartDate] = useState(start);
  const [untilDate, setUntilDate] = useState(end);

  const [pickerType, setPickerType] = useState(datePickerType);

  // keep callbacks stable to avoid effect loops
  const onSelectStartDateRef = React.useRef(onSelectStartDate);
  const onSelectEndDateRef = React.useRef(onSelectEndDate);
  const setDatePickerTypeRef = React.useRef(setDatePickerType);

  useEffect(() => {
    onSelectStartDateRef.current = onSelectStartDate;
  }, [onSelectStartDate]);

  useEffect(() => {
    onSelectEndDateRef.current = onSelectEndDate;
  }, [onSelectEndDate]);

  useEffect(() => {
    setDatePickerTypeRef.current = setDatePickerType;
  }, [setDatePickerType]);

  useEffect(() => {
    setPickerType((prev) => (prev === datePickerType ? prev : datePickerType));
  }, [datePickerType]);

  const updatePickerType = (next: 'start' | 'end' | 'range') => {
    setPickerType(next);
    setDatePickerTypeRef.current?.(next);
  };

  useEffect(() => {
    onSelectStartDateRef.current?.(startDate ? dayjs(startDate) : undefined);
  }, [startDate]);

  useEffect(() => {
    onSelectEndDateRef.current?.(untilDate ? dayjs(untilDate) : undefined);
  }, [untilDate]);

  // Rebuild month grid when (year, month) changes
  useEffect(() => {
    GetMonthData(year, month);
  }, [year, month]);

  // Rebuild weeks when selection changes (range background depends on it)

  const GetMonthData = (year: number, month: number) => {
    const daysCurrent = GetDays(year, month);
    setDaysInMonth(daysCurrent);

    const currentMonthDays: number[] = [];

    // Compute previous month year & month
    const prevYear = month === 0 ? year - 1 : year;
    const prevMonth = month === 0 ? 11 : month - 1;
    const daysPrev = GetDays(prevYear, prevMonth);

    const firstDay = new Date(year, month, 1);
    const week = firstDay.getDay(); // 0..6

    // -------------------------------
    // 1) Leading days: last days of previous month
    //    We store them as negative numbers: -actualDay
    // -------------------------------
    for (let i = 0; i < week; i++) {
      const dayNumber = daysPrev - week + 1 + i; // e.g. 29,30
      currentMonthDays.push(-dayNumber);
    }

    // -------------------------------
    // 2) Actual current month days: 1..daysCurrent (positive)
    // -------------------------------
    for (let i = 1; i <= daysCurrent; i++) {
      currentMonthDays.push(i);
    }

    // -------------------------------
    // 3) Trailing days: first days of next month
    //    We store them as > daysCurrent (daysCurrent + n)
    //    and ensure there are always 6 rows (6 * 7 = 42 cells)
    // -------------------------------
    let nextDay = 1;
    while (currentMonthDays.length < 42) {
      currentMonthDays.push(daysCurrent + nextDay);
      nextDay++;
    }

    setCurrentMonthDay(currentMonthDays);
  };

  const onSelectDay = (day: number, xMonth: number, xYear: number) => {
    // day + xMonth + xYear
    const dateSelected = new Date(xYear, xMonth, day);

    if (pickerType === 'start') {
      setStartDate(dateSelected);
      if (untilDate && untilDate < dateSelected) setUntilDate(undefined);
      updatePickerType('end');
      return;
    } else if (pickerType === 'end') {
      if (startDate && startDate > dateSelected) setStartDate(undefined);
      setUntilDate(dateSelected);
      return;
    }

    if (!startDate && !untilDate) {
      setStartDate(dateSelected);
      return;
    }

    // Case 2: have start but no end
    if (startDate && !untilDate) {
      if (isSameDay(dateSelected, startDate)) {
        // Tap same day again => clear
        setStartDate(undefined);
        return;
      }

      if (dayjs(dateSelected).isBefore(startDate, 'day')) {
        // New date before start => move start
        setStartDate(dateSelected);
      } else {
        // New date after start => set as end
        setUntilDate(dateSelected);
      }
      return;
    }

    // Case 3: both start & end exist => restart from this date
    setStartDate(dateSelected);
    setUntilDate(undefined);
  };

  const monthPre = () => {
    if (month == 0) {
      setMonth(11);
      setYear(year - 1);
    } else {
      setMonth(month - 1);
    }
  };

  const monthNext = () => {
    if (month == 11) {
      setMonth(0);
      setYear(year + 1);
    } else {
      setMonth(month + 1);
    }
  };

  const isSameDay = (d1?: Date, d2?: Date) =>
    !!(d1 && d2 && dayjs(d1).isSame(d2, 'day'));

  const getWeek = (days: number[], daysInCurrent: number) => {
    const _weeks: JSX.Element[] = [];
    let week: JSX.Element[] = [];

    const hasBoth =
      !!startDate && !!untilDate && dayjs(startDate).isBefore(untilDate, 'day');
    const hasOnlyStart = !!startDate && !untilDate;
    const hasOnlyEnd = !!untilDate && !startDate;

    for (let i = 1; i <= days.length; i++) {
      const raw = days[i - 1];

      const isPrevMonth = raw < 0;
      const isNextMonth = raw > daysInCurrent;
      const isCurrentMonthDay = !isPrevMonth && !isNextMonth && raw > 0;

      let xMonth = month;
      let xYear = year;

      if (isPrevMonth) {
        if (month === 0) {
          xMonth = 11;
          xYear = year - 1;
        } else {
          xMonth = month - 1;
        }
      } else if (isNextMonth) {
        if (month === 11) {
          xMonth = 0;
          xYear = year + 1;
        } else {
          xMonth = month + 1;
        }
      }

      // Display number:
      //  prev month: abs(raw)
      //  current month: raw
      //  next month: raw - daysInCurrent
      let displayNumber = 0;
      if (isPrevMonth) {
        displayNumber = Math.abs(raw);
      } else if (isNextMonth) {
        displayNumber = raw - daysInCurrent;
      } else if (isCurrentMonthDay) {
        displayNumber = raw;
      }

      const dayDate =
        displayNumber > 0 ? new Date(xYear, xMonth, displayNumber) : undefined;

      const isStart = dayDate && isSameDay(dayDate, startDate);
      const isEnd = dayDate && isSameDay(dayDate, untilDate);
      const isToday =
        dayDate && dayjs(dayDate).isSame(beginningOfToday(), 'day');

      const inRange =
        hasBoth &&
        !!dayDate &&
        dayjs(dayDate).isAfter(startDate!, 'day') &&
        dayjs(dayDate).isBefore(untilDate!, 'day');

      const showFullGrey = hasBoth && inRange;
      const showHalfRightGrey = (hasBoth || hasOnlyStart) && !!isStart;
      const showHalfLeftGrey = (hasBoth || hasOnlyEnd) && !!isEnd;

      const isSelectedCircle = !!(isStart || isEnd);

      week.push(
        <DayCell
          key={year + month + 'col' + i}
          displayNumber={displayNumber}
          isPrevMonth={isPrevMonth}
          isNextMonth={isNextMonth}
          isSelectedCircle={isSelectedCircle}
          isToday={!!isToday}
          showFullGrey={showFullGrey}
          showHalfRightGrey={showHalfRightGrey}
          showHalfLeftGrey={showHalfLeftGrey}
          onPress={() => {
            if (displayNumber > 0) onSelectDay(displayNumber, xMonth, xYear);
          }}
        />
      );

      if (i % 7 === 0) {
        _weeks.push(
          <Box key={year + month + 'week' + i} style={styles.row}>
            {week}
          </Box>
        );
        week = [];
      }
    }

    return _weeks;
  };

  const weeks = React.useMemo(() => {
    return getWeek(currentMonthDay, daysInMonth);
  }, [
    currentMonthDay,
    daysInMonth,
    startDate,
    untilDate,
    pickerType,
    month,
    year,
  ]);

  return (
    <Box flex={0.7}>
      <Box style={styles.row} alignItems='center' height={44}>
        <Box style={styles.dayColumn}>
          <WebIcon
            onPress={monthPre}
            name='ChevronLeft'
            variant='l'
            color='textPrimary'
            accessibilityLabel='Previous month'
          />
        </Box>

        <Box style={[styles.dayColumn, { flex: 5 }]}>
          <Text textAlign='center'>
            {MONTH_NAME[month]} {year}
          </Text>
        </Box>

        <Box style={styles.dayColumn}>
          <WebIcon
            onPress={monthNext}
            name='ChevronRight'
            variant='l'
            color='textPrimary'
            accessibilityLabel='Next month'
          />
        </Box>
      </Box>

      <Box style={styles.row}>
        {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, index) => (
          <Box key={d + index.toString()} style={styles.dayColumn}>
            <Box style={styles.weekdayCellWrapper}>
              <Text variant='webSmall'>{d}</Text>
            </Box>
          </Box>
        ))}
      </Box>

      {weeks}
      {!hideFooter && (
        <Box
          borderTopWidth={1}
          borderTopColor='grey02'
          flexDirection='row'
          alignItems='center'
          pt='s'
          mb='s'
          height={getCommonScaledDimensions().calendarFooter}
          left={0}
          right={0}
          px='m'>
          <Box flex={1}>
            <ClearDateButton
              hoverDisabled={!startDate && !untilDate}
              onPress={() => {
                if (datePickerType === 'range') {
                  setStartDate(undefined);
                  setUntilDate(undefined);
                } else if (datePickerType === 'start') {
                  setStartDate(undefined);
                } else {
                  setUntilDate(undefined);
                }
              }}
              cursor={startDate || untilDate ? 'pointer' : 'not-allowed'}
              width={125}
              height={48}
              label={clearLabel}
            />
          </Box>
          <Box flex={1} marginLeft='m'>
            <Button
              fullWidth={true}
              height={48}
              disabled={isSubmitting}
              onPress={() => {
                if (!isSubmitting) {
                  onSelect(startDate, untilDate);
                }
              }}
              isSmall={true}
              borderRadius='xl'
              variant='primary'
              accessibilityLabel={confirmLabel ?? t('shared:confirm')}>
              {isSubmitting ? (
                <ActivityIndicator style={{ height: 16 }} />
              ) : (
                <Text variant='labelSmall' color='white'>
                  {confirmLabel ?? t('shared:confirm')}
                </Text>
              )}
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    paddingHorizontal: ROW_PADDING_X,
    backgroundColor: theme.colors.white,
  },

  selectedCircle: {
    position: 'absolute',
    width: CELL_SIZE,
    height: CELL_SIZE,
    borderRadius: CELL_RADIUS,
    backgroundColor: theme.colors.black,
  },

  todayOutline: {
    position: 'absolute',
    width: CELL_SIZE,
    height: CELL_SIZE,
    borderRadius: CELL_RADIUS,
    borderWidth: 1,
    borderColor: theme.colors.grey05,
  },

  dayLabel: {
    textAlign: 'center',
    textAlignVertical: 'center' as any,
    color: theme.colors.textSecondary,
    zIndex: 2,
  },

  dayLabelOutsideMonth: {
    color: theme.colors.grey04,
  },

  dayLabelSelected: {
    color: theme.colors.white,
    fontWeight: '600',
  },

  weekdayCellWrapper: {
    width: CELL_SIZE,
    height: 32,
    alignItems: 'center',
    justifyContent: 'center',
  },

  ayColumn: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
  },

  dayCellWrapper: {
    width: CELL_SIZE,
    height: CELL_SIZE,
    marginVertical: 4,
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
  },

  rangeBackgroundBase: {
    position: 'absolute',
    top: 4,
    bottom: 4,
    backgroundColor: theme.colors.grey02,
  },

  rangeBackgroundFull: {
    left: 0,
    right: 0,
  },

  rangeBackgroundRightHalf: {
    left: '50%',
    right: 0,
  },
  headerTitle: {
    flex: 1,
    alignItems: 'center',
  },

  rangeBackgroundLeftHalf: {
    left: 0,
    right: '50%',
  },
  dayColumn: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
  },
});
